import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// third-party
import { Provider as ReduxProvider } from 'react-redux';

// apex-chart
import './assets/third-party/apex-chart.css';

// project import
import App from './App';
import { store } from './store';
import reportWebVitals from './reportWebVitals';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { createAuthLink, AUTH_TYPE } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { ApolloProvider, ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './components/ErrorBoundary/ErrorFallback';
import { logger } from './utils/logger';

Amplify.configure({
    Auth: {
        Cognito: {
            identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID,
            userPoolId: import.meta.env.VITE_USER_POOL_ID,
            userPoolClientId: import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID,
            allowGuestAccess: true,
            signUpVerificationMethod: 'code' // Enables auto sign-in after email verification
        }
    }
});

//Apollo
const url = import.meta.env.VITE_APPSYNC_CUSTOM_GRAPHQL_ENDPOINT;
const region = import.meta.env.VITE_AWS_REGION;
const auth = {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS as const,
    jwtToken: async () => {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken?.toString() || '';
        } catch (e) {
            logger.log('Error getting token:', e);
            return '';
        }
    }
};
const removeTypenameLink = removeTypenameFromVariables();
const httpLink = new HttpLink({ uri: url });

// Error link to handle 401 unauthorized errors (stale tokens, etc.)
let isRedirectingTo401 = false;
const errorLink = onError(({ networkError }) => {
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
        // Prevent redirect loop - only redirect once and not if already on login/landing pages
        const currentPath = window.location.pathname;
        // Normalize path by removing trailing slash for comparison
        const normalizedPath = currentPath.endsWith('/') && currentPath !== '/' ? currentPath.slice(0, -1) : currentPath;
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/', '/terms', '/contact_us'];
        const isPublicPath = publicPaths.some((path) => normalizedPath === path || normalizedPath.startsWith('/login'));

        if (!isRedirectingTo401 && !isPublicPath) {
            isRedirectingTo401 = true;
            logger.log('401 Unauthorized - clearing session and redirecting to login');
            // Clear storage
            localStorage.clear();
            sessionStorage.clear();
            // Sign out from Amplify
            signOut().catch((err) => console.error('Error signing out:', err));
            // Redirect to login page
            window.location.href = '/login';
        }
    }
});

const link = ApolloLink.from([
    errorLink,
    removeTypenameLink,
    createAuthLink({ url, region, auth }),
    createSubscriptionHandshakeLink({ url, region, auth }, httpLink)
]);
const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
        typePolicies: {
            Route: {
                fields: {
                    actionLog: {
                        merge(existing = [], incoming) {
                            // If incoming exists and is an array, prepend all new items to existing
                            if (Array.isArray(incoming) && incoming.length > 0) {
                                // Prepend all incoming items to existing array
                                return [...incoming, ...existing];
                            }
                            // If no incoming data, keep existing
                            return existing;
                        }
                    },
                    stops: {
                        merge(existing, incoming) {
                            // Always replace with incoming data if it exists
                            return incoming || existing || [];
                        }
                    },
                    totalStops: {
                        merge(existing, incoming) {
                            // Don't overwrite existing value with null/undefined
                            if (incoming === null || incoming === undefined) {
                                return existing;
                            }
                            return incoming;
                        }
                    }
                }
            }
        }
    }),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-first',
            nextFetchPolicy: 'cache-first'
        },
        query: {
            fetchPolicy: 'cache-first'
        }
    }
});

// ==============================|| MAIN - REACT DOM RENDER  ||============================== //

//Amplify.Logger.LOG_LEVEL = 'VERBOSE';

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
const root = createRoot(container);

function logError(error: unknown, info: { componentStack?: string | null }) {
    console.error('Error caught by boundary:', error, info);
}

root.render(
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={logError} onReset={() => window.location.reload()}>
        <Authenticator.Provider>
            <ApolloProvider client={client}>
                <ReduxProvider store={store}>
                    <BrowserRouter
                        basename="/"
                        future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true
                        }}
                    >
                        <App />
                    </BrowserRouter>
                </ReduxProvider>
            </ApolloProvider>
        </Authenticator.Provider>
    </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
